import { useState, useMemo } from 'react';

// Service de gestion des devises et conversions
export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  rate: number; // Taux de change par rapport à EUR
}

// Devises supportées avec leurs taux de change (base GBP = 1.0)
export const SUPPORTED_CURRENCIES: Record<string, CurrencyInfo> = {
  // Europe
  GBP: {
    code: 'GBP',
    symbol: '£',
    name: 'British Pound',
    rate: 1.0, // Devise de référence
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    name: 'EUR',
    rate: 1.14, // 1 GBP = 1.14 EUR (aligned with Stripe prices)
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    name: 'CHF',
    rate: 1.09, // 1 GBP = 1.09 CHF
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    name: 'Polish Złoty',
    rate: 4.91, // 1 GBP = 4.91 PLN (aligned with Stripe prices)
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    name: 'Swedish Krona',
    rate: 12.3, // 1 GBP = 12.3 SEK (aligned with Stripe prices)
  },
  DKK: {
    code: 'DKK',
    symbol: 'kr',
    name: 'Danish Krone',
    rate: 8.68, // 1 GBP = 8.68 DKK (aligned with Stripe prices)
  },
  NOK: {
    code: 'NOK',
    symbol: 'kr',
    name: 'Norwegian Krone',
    rate: 13.17, // 1 GBP = 13.17 NOK (aligned with Stripe prices)
  },
  CZK: {
    code: 'CZK',
    symbol: 'Kč',
    name: 'Czech Koruna',
    rate: 27.81, // 1 GBP = 27.81 CZK (aligned with Stripe prices)
  },
  HUF: {
    code: 'HUF',
    symbol: 'Ft',
    name: 'HUF',
    rate: 431.58, // 1 GBP = 431.58 HUF (aligned with Stripe prices)
  },
  RON: {
    code: 'RON',
    symbol: 'lei',
    name: 'RON',
    rate: 5.93, // 1 GBP = 5.93 RON (aligned with Stripe prices)
  },
  // BGN, RUB, UAH removed - not supported by Stripe
  TRY: {
    code: 'TRY',
    symbol: '₺',
    name: 'Turkish Lira',
    rate: 59.41, // 1 GBP = 59.41 TRY (aligned with Stripe prices)
  },
  
  // Amérique
  USD: {
    code: 'USD',
    symbol: '$',
    name: 'USD',
    rate: 1.38, // 1 GBP = 1.38 USD (aligned with Stripe prices)
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    name: 'CAD',
    rate: 1.87, // 1 GBP = 1.87 CAD (aligned with Stripe prices)
  },
  MXN: {
    code: 'MXN',
    symbol: '$',
    name: 'Mexican Peso',
    rate: 23.46, // 1 GBP = 23.46 MXN (aligned with Stripe prices)
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    name: 'BRL',
    rate: 7.09, // 1 GBP = 7.09 BRL (aligned with Stripe prices)
  },
  
  // Asie
  JPY: {
    code: 'JPY',
    symbol: '¥',
    name: 'JPY',
    rate: 213.04, // 1 GBP = 213.04 JPY (aligned with Stripe prices)
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    name: 'CNY',
    rate: 9.55, // 1 GBP = 9.55 CNY (aligned with Stripe prices)
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    name: 'Korean Won',
    rate: 2000.0, // 1 GBP = 2000 KRW (aligned with Stripe prices)
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    name: 'Indian Rupee',
    rate: 124.62, // 1 GBP = 124.62 INR (aligned with Stripe prices)
  },
  PKR: {
    code: 'PKR',
    symbol: '₨',
    name: 'Pakistani Rupee',
    rate: 380.57, // 1 GBP = 380.57 PKR (aligned with Stripe prices)
  },
  THB: {
    code: 'THB',
    symbol: '฿',
    name: 'Thai Baht',
    rate: 42.45, // 1 GBP = 42.45 THB (aligned with Stripe prices)
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    name: 'Singapore Dollar',
    rate: 1.75, // 1 GBP = 1.75 SGD (aligned with Stripe prices)
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    name: 'Malaysian Ringgit',
    rate: 5.49, // 1 GBP = 5.49 MYR (aligned with Stripe prices)
  },
  IDR: {
    code: 'IDR',
    symbol: 'Rp',
    name: 'Indonesian Rupiah',
    rate: 22884.06, // 1 GBP = 22884.06 IDR (aligned with Stripe prices)
  },
  PHP: {
    code: 'PHP',
    symbol: '₱',
    name: 'Philippine Peso',
    rate: 79.7, // 1 GBP = 79.7 PHP (aligned with Stripe prices)
  },
  VND: {
    code: 'VND',
    symbol: '₫',
    name: 'Vietnamese Dong',
    rate: 35072.46, // 1 GBP = 35072.46 VND (aligned with Stripe prices)
  },
  HKD: {
    code: 'HKD',
    symbol: 'HK$',
    name: 'Hong Kong Dollar',
    rate: 10.71, // 1 GBP = 10.71 HKD (aligned with Stripe prices)
  },
  
  // Moyen-Orient & Afrique
  AED: {
    code: 'AED',
    symbol: 'AED',
    name: 'UAE Dirham',
    rate: 5.06, // 1 GBP = 5.06 AED (aligned with Stripe prices)
  },
  SAR: {
    code: 'SAR',
    symbol: 'SAR',
    name: 'Saudi Riyal',
    rate: 5.2, // 1 GBP = 5.2 SAR (aligned with Stripe prices)
  },
  ILS: {
    code: 'ILS',
    symbol: '₪',
    name: 'Israeli Shekel',
    rate: 4.33, // 1 GBP = 4.33 ILS (aligned with Stripe prices)
  },
  // IRR (Iranian Rial) removed - not supported due to sanctions
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    name: 'South African Rand',
    rate: 21.72, // 1 GBP = 21.72 ZAR (aligned with Stripe prices)
  },
  EGP: {
    code: 'EGP',
    symbol: '£',
    name: 'Egyptian Pound',
    rate: 63.75, // 1 GBP = 63.75 EGP (aligned with Stripe prices)
  },
  MAD: {
    code: 'MAD',
    symbol: 'DH',
    name: 'Moroccan Dirham',
    rate: 12.45, // 1 GBP = 12.45 MAD (aligned with Stripe prices)
  },
  
  // Océanie
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    name: 'Australian Dollar',
    rate: 1.96, // 1 GBP = 1.96 AUD (aligned with Stripe prices)
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    name: 'New Zealand Dollar',
    rate: 2.3, // 1 GBP = 2.3 NZD (aligned with Stripe prices)
  },
};

// Prix de base en GBP (en pence) - Devise de référence
// IMPORTANT: Ces prix doivent correspondre à global-products.json et Stripe
export const BASE_PRICES_GBP = {
  // Alex's correct prices from Stripe (Feb 2026)
  // BROKE plan: 79€ = £69 GBP
  // PRIME plan: 199€ = £175 GBP
  'restaurant-broke': 6900,   // £69 = 79€ (BROKE - Komby hospitality)
  'restaurant-prime': 17500,  // £175 = 199€ (PRIME - Komby hospitality)
  'nightclub-prime': 17500,   // £175 = 199€ (PRIME - Komby Entertainment)
  'office-prime': 17500,      // £175 = 199€ (PRIME - Komby Office)
  // Aliases for consistency with global-products.json
  'komby-resto': 6900,        // BROKE plan
  'komby-office': 17500,      // PRIME plan
  'komby-entertainment': 17500, // PRIME plan
};

/**
 * Détecte la devise préférée de l'utilisateur
 */
export function detectUserCurrency(): string {
  // 1. Vérifier le localStorage
  const stored = localStorage.getItem('preferred-currency');
  if (stored && SUPPORTED_CURRENCIES[stored]) {
    return stored;
  }

  // 2. Détecter via la locale du navigateur
  const locale = navigator.language || 'en-GB'; // GB par défaut pour société britannique
  
  // Mapping des locales vers les devises
  const localeToCurrency: Record<string, string> = {
    // Europe
    'en-GB': 'GBP', // UK prioritaire
    'fr-FR': 'EUR',
    'fr-BE': 'EUR',
    'fr-CH': 'CHF',
    'de-DE': 'EUR',
    'de-CH': 'CHF',
    'de-AT': 'EUR',
    'it-IT': 'EUR',
    'es-ES': 'EUR',
    'es-MX': 'MXN',
    'pt-PT': 'EUR',
    'nl-NL': 'EUR',
    'pl-PL': 'PLN',
    'sv-SE': 'SEK',
    'da-DK': 'DKK',
    'nb-NO': 'NOK',
    'nn-NO': 'NOK',
    'fi-FI': 'EUR',
    'cs-CZ': 'CZK',
    'sk-SK': 'EUR',
    'hu-HU': 'HUF',
    'ro-RO': 'RON',
    'bg-BG': 'BGN',
    'hr-HR': 'EUR',
    'sl-SI': 'EUR',
    'et-EE': 'EUR',
    'lv-LV': 'EUR',
    'lt-LT': 'EUR',
    'el-GR': 'EUR',
    'mt-MT': 'EUR',
    'cy-GB': 'GBP',
    // SANCTIONS: temporarily hidden - Russia
    // 'ru-RU': 'RUB',
    'tr-TR': 'TRY',
    'uk-UA': 'UAH',
    
    // Amérique
    'en-US': 'USD',
    'en-CA': 'CAD',
    'pt-BR': 'BRL',
    
    // Asie
    'ja-JP': 'JPY',
    'zh-CN': 'CNY',
    'ko-KR': 'KRW',
    'hi-IN': 'INR',
    'ur-PK': 'PKR',
    'th-TH': 'THB',
    'ms-MY': 'MYR',
    'id-ID': 'IDR',
    'tl-PH': 'PHP',
    'vi-VN': 'VND',
    'en-SG': 'SGD',
    
    // Moyen-Orient & Afrique
    'ar-AE': 'AED',
    'ar-SA': 'SAR',
    'he-IL': 'ILS',
    // SANCTIONS: temporarily hidden - Iran
    // 'fa-IR': 'IRR',
    'en-ZA': 'ZAR',
    'ar-EG': 'EGP',
    'ar-MA': 'MAD',
    
    // Océanie
    'en-AU': 'AUD',
    'en-NZ': 'NZD',
  };

  const detectedCurrency = localeToCurrency[locale] || 'GBP'; // GBP par défaut
  
  // Sauvegarder la détection
  localStorage.setItem('preferred-currency', detectedCurrency);
  
  return detectedCurrency;
}

/**
 * Convertit un prix de EUR vers une autre devise
 */
export function convertPrice(priceInGbpPence: number, targetCurrency: string): number {
  const currency = SUPPORTED_CURRENCIES[targetCurrency];
  if (!currency) {
    console.warn(`Devise non supportée: ${targetCurrency}, utilisation de GBP`);
    return priceInGbpPence;
  }

  const convertedPrice = Math.round(priceInGbpPence * currency.rate);
  
  // Arrondir à des valeurs "propres" selon la devise
  return roundToNicePrice(convertedPrice, targetCurrency);
}

/**
 * Arrondit les prix à des valeurs "propres" selon la devise
 */
function roundToNicePrice(priceInCents: number, currency: string): number {
  // Devises sans décimales - arrondir à des valeurs entières
  const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR'];
  
  if (noDecimalCurrencies.includes(currency)) {
    // Pour les devises sans décimales, on travaille avec des entiers
    const amount = Math.round(priceInCents / 100);
    
    // Arrondir à des valeurs "propres"
    if (amount < 100) {
      return Math.round(amount / 10) * 10 * 100; // Arrondir à la dizaine
    } else if (amount < 1000) {
      return Math.round(amount / 50) * 50 * 100; // Arrondir à 50
    } else {
      return Math.round(amount / 100) * 100 * 100; // Arrondir à 100
    }
  }
  
  // Pour les devises avec décimales
  const priceInUnits = priceInCents / 100;
  
  switch (currency) {
    case 'USD':
    case 'CAD':
    case 'SGD':
      // Arrondir à .99 ou .95
      if (priceInUnits < 50) {
        return Math.round(priceInUnits) * 100 - 1; // X.99
      } else {
        return Math.round(priceInUnits / 5) * 5 * 100 - 1; // X5.99
      }
      
    case 'GBP':
      // Arrondir à .99 ou .95
      return Math.round(priceInUnits) * 100 - 1;
      
    case 'CHF':
      // Arrondir à .90 ou .95
      return Math.round(priceInUnits) * 100 - 10;
      
    case 'EUR':
    default:
      // Garder le prix original ou arrondir légèrement
      return Math.round(priceInCents / 10) * 10; // Arrondir à la dizaine de centimes
  }
}

/**
 * Formate un prix selon la devise
 */
/**
 * Arrondit un prix annuel vers le haut pour éviter les décimales
 * Formule : tarif mensuel × 12 × 0.9, arrondi au-dessus (Math.ceil)
 * Exemple: 2,256.66 → Math.ceil(2,256.66) = 2,257
 * Exemple: 43,092.0 → Math.ceil(43,092.0) = 43,092 (déjà entier)
 * 
 * @param priceInCents Prix en centimes (résultat de mensuel × 12 × 0.9)
 * @returns Prix arrondi vers le haut en centimes (sans décimales)
 */
export function roundYearlyPriceUp(priceInCents: number): number {
  // Convertir en unités, arrondir au-dessus, puis reconvertir en centimes
  const amount = priceInCents / 100; // Convertir en unités
  const rounded = Math.ceil(amount); // Arrondir au-dessus pour éviter les décimales
  return rounded * 100; // Retourner en centimes
}

export function formatPrice(priceInCents: number, currency: string): string {
  const currencyInfo = SUPPORTED_CURRENCIES[currency] || SUPPORTED_CURRENCIES.GBP;
  
  // Devises sans décimales
  const noDecimalCurrencies = ['JPY', 'KRW', 'VND', 'IDR'];
  
  if (noDecimalCurrencies.includes(currency)) {
    // Pour les devises sans décimales, diviser par 100 et arrondir
    const amount = Math.round(priceInCents / 100);
    return `${currencyInfo.symbol}${amount.toLocaleString()}`;
  }
  
  // Pour les autres devises
  const price = priceInCents / 100;
  
  // Formatage selon la devise
  // Arrondir à l'entier pour toutes les devises (marketing pricing)
  const roundedPrice = Math.round(price);
  
  switch (currency) {
    case 'EUR':
      return `${roundedPrice.toLocaleString('en-US')} ${currencyInfo.symbol}`;
    case 'USD':
    case 'CAD':
    case 'SGD':
    case 'AUD':
    case 'NZD':
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
    case 'GBP':
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
    case 'CHF':
      return `${roundedPrice.toLocaleString('en-US')} ${currencyInfo.symbol}`;
    case 'AED':
    case 'SAR':
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
    case 'ILS':
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
    case 'JPY':
    case 'KRW':
    case 'VND':
    case 'IDR':
      // Déjà géré plus haut, mais au cas où
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
    default:
      // Format par défaut : symbole avant le montant
      return `${currencyInfo.symbol}${roundedPrice.toLocaleString('en-US')}`;
  }
}

/**
 * Obtient les prix convertis pour tous les produits
 */
export function getConvertedPrices(currency: string) {
  const result: Record<string, { original: number; converted: number; formatted: string }> = {};
  
  Object.entries(BASE_PRICES_GBP).forEach(([productKey, priceGbp]) => {
    const converted = convertPrice(priceGbp, currency);
    result[productKey] = {
      original: priceGbp,
      converted,
      formatted: formatPrice(converted, currency),
    };
  });
  
  return result;
}

/**
 * Hook React pour la gestion des devises - DEPRECATED
 * Utilisez CurrencyProvider et useCurrency du contexte à la place
 */
export function useCurrencyDeprecated() {
  const [currency, setCurrency] = useState<string>(() => detectUserCurrency());
  
  const changeCurrency = (newCurrency: string) => {
    if (SUPPORTED_CURRENCIES[newCurrency]) {
      setCurrency(newCurrency);
      localStorage.setItem('preferred-currency', newCurrency);
    }
  };
  
  const prices = useMemo(() => getConvertedPrices(currency), [currency]);
  
  return {
    currency,
    currencyInfo: SUPPORTED_CURRENCIES[currency],
    changeCurrency,
    prices,
    supportedCurrencies: SUPPORTED_CURRENCIES,
  };
}